if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.cartAddInFlight = false;
        this.cartAddPromise = null;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.form?.querySelector('.add-to-cart-button');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      buttonLooksLikeCustomize(button) {
        if (!button) return false;
        const label = (
          button.querySelector('.add-to-cart-text__content, .add-to-cart-text')?.textContent ||
          button.textContent ||
          ''
        )
          .trim()
          .toLowerCase();
        return label.includes('customize');
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      isCustomizeNowIntent(evt) {
        const submitter = evt?.submitter;
        const label = (
          (submitter &&
            (submitter.querySelector?.('.add-to-cart-text__content, .add-to-cart-text')?.textContent ||
              submitter.textContent)) ||
          this.submitButtonText?.textContent ||
          this.submitButton?.querySelector('.add-to-cart-text__content, .add-to-cart-text')?.textContent ||
          this.submitButton?.textContent ||
          ''
        )
          .trim()
          .toLowerCase();

        // Hard gate: visible Add to cart must never open Customeow / block submit.
        if (
          label.includes('add to cart') ||
          label.includes('add to bag') ||
          label.includes('sold out') ||
          label.includes('unavailable')
        ) {
          return false;
        }

        if (submitter && this.buttonLooksLikeCustomize(submitter)) return true;
        if (label.includes('customize') || label.includes('customise')) return true;

        // With-name only counts when this SKU is actually Customeow-connected.
        if (document.documentElement.classList.contains('pdp--with-name')) {
          const sync = window.PdpNameVariantSync;
          if (sync && typeof sync.isSkuConnectedToCustomeow === 'function') {
            return sync.isSkuConnectedToCustomeow(this.closest('product-info'));
          }
          return false;
        }
        return false;
      }

      hasCustomizationProperties() {
        const isMeaningfulValue = (value) => {
          if (value == null) return false;
          const trimmed = String(value).trim();
          if (!trimmed || trimmed === 'undefined' || trimmed === 'null' || trimmed === 'false') return false;
          return true;
        };

        const isPersonalizationKey = (name) => {
          if (!name || !name.startsWith('properties[')) return false;
          const key = name.slice('properties['.length, -1).trim().toLowerCase();
          if (!key || key.startsWith('_')) return false;
          // Require strong completion signals — placeholder Customeow fields must not count.
          if (key.startsWith('preview') && (key.includes('image') || key.includes('url') || key.includes('effect'))) {
            return true;
          }
          if (key.includes('custominfoid') || key.includes('custom_info_id')) return true;
          if (key === 'name' || key === 'name (arabic)' || key === 'name arabic') return true;
          if (key.includes('personalization') || key.includes('personalisation')) return true;
          return false;
        };

        const consider = (name, value, isFile) => {
          if (!isPersonalizationKey(name)) return false;
          if (isFile) return true;
          if (!isMeaningfulValue(value)) return false;
          const key = name.slice('properties['.length, -1).trim().toLowerCase();
          // customInfoId placeholders are often numeric stubs — require preview/name too later
          if (key.includes('custominfoid') || key.includes('custom_info')) {
            return /https?:\/\//i.test(String(value)) || String(value).length > 8;
          }
          if (key.startsWith('preview')) return /https?:\/\//i.test(String(value)) || String(value).length > 12;
          return true;
        };

        let hasPreviewOrName = false;
        let hasCustomInfo = false;

        const scan = (name, value, isFile) => {
          if (!consider(name, isFile ? '1' : value, isFile)) return;
          const key = name.slice('properties['.length, -1).trim().toLowerCase();
          if (key.includes('custominfoid') || key.includes('custom_info')) hasCustomInfo = true;
          else hasPreviewOrName = true;
        };

        if (this.form) {
          const formData = new FormData(this.form);
          for (const [key, value] of formData.entries()) {
            const isFile = typeof File !== 'undefined' && value instanceof File;
            scan(key, isFile ? '1' : value, isFile);
          }
        }

        this.getExternalPropertyScopes().forEach((scope) => {
          scope.querySelectorAll?.('[name^="properties["]').forEach((input) => {
            if (this.form?.contains(input)) return;
            if (input.type === 'file') {
              if (input.files?.length) scan(input.name, '1', true);
              return;
            }
            scan(input.name, input.value, false);
          });
        });

        // Complete personalization = preview/name present (optionally with custom info).
        return hasPreviewOrName || hasCustomInfo;
      }

      clearSubmitLoading() {
        const releaseButton = (button) => {
          if (!button || button.dataset.themeCartAddLock !== 'true') return;

          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
          button.removeAttribute('aria-busy');

          if (button.dataset.themeCartAddWasDisabled !== 'true' && !button.hasAttribute('data-hadiyah-delivery-disabled')) {
            button.disabled = false;
          }
          if (
            button.dataset.themeCartAddPreviousAriaDisabled !== 'true' &&
            !button.hasAttribute('data-hadiyah-delivery-disabled')
          ) {
            button.removeAttribute('aria-disabled');
          }

          delete button.dataset.themeCartAddLock;
          delete button.dataset.themeCartAddWasDisabled;
          delete button.dataset.themeCartAddPreviousAriaDisabled;
        };

        releaseButton(this.submitButton);

        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId
          ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
          : undefined;
        releaseButton(stickyAddButton);
      }

      setSubmitLoading() {
        const lockButton = (button) => {
          if (!button || button.dataset.themeCartAddLock === 'true') return;
          button.dataset.themeCartAddLock = 'true';
          button.dataset.themeCartAddWasDisabled = String(button.disabled);
          button.dataset.themeCartAddPreviousAriaDisabled = String(
            button.getAttribute('aria-disabled') === 'true'
          );
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
          button.classList.add('loading');
          button.querySelector('.loading__spinner')?.classList.remove('hidden');
        };

        lockButton(this.submitButton);

        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId
          ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
          : undefined;
        lockButton(stickyAddButton);
      }

      dispatchCartAddLifecycle(phase, detail = {}) {
        this.dispatchEvent(
          new CustomEvent(`product-form:cart-add-${phase}`, {
            bubbles: true,
            detail: {
              source: 'product-form-component',
              status: detail.status || phase,
              message: detail.message || '',
            },
          })
        );
      }

      publishCartError(formData, message, response = {}) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form-component',
            productVariantId: formData?.get?.('id') || this.variantIdInput?.value,
            errors: response.errors || response.description || message,
            message: response.message || message,
          });
        }
      }

      getExternalPropertyScopes() {
        const scopes = [];
        const productInfo = this.closest('product-info');
        if (productInfo) scopes.push(productInfo);

        const customeowSurface = window.PdpNameVariantSync?.getActiveCustomeowSurface?.();
        if (customeowSurface && !scopes.includes(customeowSurface)) scopes.push(customeowSurface);
        return scopes;
      }

      appendExternalCustomizationProperties(formData) {
        const seen = new Set(Array.from(formData.keys()));
        this.getExternalPropertyScopes().forEach((scope) => {
          scope.querySelectorAll?.('.product-form__input [name^="properties["]').forEach((property) => {
            if (this.form.contains(property) || !property.name || seen.has(property.name)) return;

            if (property.type === 'file') {
              if (!property.files?.[0]) return;
              formData.append(property.name, property.files[0]);
              seen.add(property.name);
              return;
            }

            if (property.value == null || property.value === '') return;
            formData.append(property.name, property.value);
            seen.add(property.name);
          });
        });
      }

      dedupeLineProperties(formData) {
        if (typeof formData.getAll !== 'function' || typeof formData.delete !== 'function') return;
        const propertyNames = new Set(
          Array.from(formData.keys()).filter((name) => name.startsWith('properties['))
        );
        propertyNames.forEach((name) => {
          const values = formData.getAll(name);
          if (values.length <= 1) return;
          const value = values[values.length - 1];
          formData.delete(name);
          formData.append(name, value);
        });
      }

      isCustomizationPropertyName(name) {
        if (!name?.startsWith?.('properties[')) return false;
        const key = name.slice('properties['.length, -1).trim().toLowerCase();
        return (
          key === 'name' ||
          key === 'name (arabic)' ||
          key === 'name arabic' ||
          key.includes('custominfoid') ||
          key.includes('custom_info_id') ||
          key.includes('personalization') ||
          key.includes('personalisation') ||
          (key.startsWith('preview') &&
            (key.includes('image') || key.includes('url') || key.includes('effect')))
        );
      }

      clearSubmittedCustomizationProperties() {
        const controls = [];
        this.getExternalPropertyScopes().forEach((scope) => {
          scope.querySelectorAll?.('[name^="properties["]').forEach((control) => {
            if (!controls.includes(control)) controls.push(control);
          });
        });
        this.form.querySelectorAll('[name^="properties["]').forEach((control) => {
          if (!controls.includes(control)) controls.push(control);
        });
        controls.forEach((control) => {
          if (this.isCustomizationPropertyName(control.name)) control.remove();
        });
      }

      scheduleCheckoutPreparation() {
        const prepare = () => {
          window.__paymentIntentDetected = true;
          window.__mountDeferredPayments?.();
          window.setTimeout(() => window.__loadGokwikMerchant?.(), 0);
        };

        // Let the disabled/loading state paint before mounting merchant scripts.
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(prepare);
        else window.setTimeout(prepare, 0);
      }

      async parseCartAddResponse(response) {
        let data;
        try {
          data = await response.json();
        } catch (error) {
          throw new Error('We could not confirm the cart update. Please check your cart before trying again.');
        }

        if (response.ok === false || data?.status || data?.errors) {
          const error = new Error(data?.description || data?.message || 'Unable to add this item. Please try again.');
          error.cartResponse = data || {};
          throw error;
        }
        if (!data || typeof data !== 'object') {
          throw new Error('We could not confirm the cart update. Please check your cart before trying again.');
        }
        return data;
      }

      async requestCartAdd(url, config) {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timeout = controller
          ? window.setTimeout(() => controller.abort(), 30000)
          : null;
        if (controller) config.signal = controller.signal;
        try {
          const response = await fetch(url, config);
          const result = await this.parseCartAddResponse(response);
          if (response.ok && config.body instanceof FormData)
            window['clean-commerce']?.added(result, [{ id: config.body.get('id'), quantity: config.body.get('quantity') || 1 }], 'legacy-product-form');
          return result;
        } finally {
          if (timeout) window.clearTimeout(timeout);
        }
      }

      isAmbiguousNetworkError(error) {
        return (
          error?.name === 'AbortError' ||
          error instanceof TypeError ||
          /failed to fetch|network|load failed/i.test(error?.message || '')
        );
      }

      getSubmittedPropertySignature(formData) {
        const properties = new Map();
        for (const [name, value] of formData.entries()) {
          if (!name.startsWith('properties[') || typeof value !== 'string') continue;
          const key = name.slice('properties['.length, -1);
          if (key) properties.set(key, value);
        }
        return properties;
      }

      getMatchingCartQuantity(cart, formData) {
        if (!cart?.items || !formData) return null;
        const variantId = String(formData.get('id') || '');
        const properties = this.getSubmittedPropertySignature(formData);
        return cart.items.reduce((total, item) => {
          if (String(item.variant_id || item.id || '') !== variantId) return total;
          const itemProperties = item.properties || {};
          for (const [key, value] of properties) {
            if (String(itemProperties[key] ?? '') !== String(value)) return total;
          }
          return total + (Number(item.quantity) || 0);
        }, 0);
      }

      captureCartBaseline(formData) {
        const cart = window.CleanCart?.getRecentCart?.()?.cart || window.cart;
        const quantity = this.getMatchingCartQuantity(cart, formData);
        return quantity == null ? null : quantity;
      }

      async reconcileAmbiguousCartAdd(formData, baselineQuantity) {
        if (baselineQuantity == null) return null;
        const cartBase = window.routes?.cart_url || '/cart';
        const cartUrl = cartBase.endsWith('.js') ? cartBase : `${cartBase}.js`;
        try {
          const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
          if (response.ok === false) return null;
          const cart = await response.json();
          const currentQuantity = this.getMatchingCartQuantity(cart, formData);
          const requestedQuantity = Math.max(1, Number(formData.get('quantity')) || 1);
          return currentQuantity != null && currentQuantity >= baselineQuantity + requestedQuantity
            ? cart
            : null;
        } catch (error) {
          return null;
        }
      }

      async openFallbackCart(cartData) {
        if (window.CleanCart?.openDrawer) {
          await window.CleanCart.openDrawer({ refresh: true });
          return;
        }
        if (this.cart && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(cartData);
          return;
        }
        if (window.routes?.cart_url) {
          window.location = window.routes.cart_url;
          return;
        }
        throw new Error('Cart drawer is not available.');
      }

      async openPostAddFlow(cartData, useAddonDrawer, addonReady) {
        if (useAddonDrawer) {
          try {
            const addon = await addonReady;
            const owner = window.CartAddonDrawer || addon;
            const opener = owner?.openWithCartResponse;
            if (typeof opener === 'function') {
              await opener.call(owner, cartData);
              return;
            }
          } catch (error) {
            console.error(error);
          }
        }
        await this.openFallbackCart(cartData);
      }

      onSubmitHandler(evt) {
        // Open Customeow only when personalization is still missing.
        // After Customeow writes properties, Add to cart must proceed (even if the
        // theme label still says Customize Now — that used to bounce users back to PDP).
        const customizeIntent = this.isCustomizeNowIntent(evt);
        const personalized = this.hasCustomizationProperties();

        if (customizeIntent && !personalized) {
          evt.preventDefault();

          // Prefer our Personalizable slide-in module when present.
          if (window.PdpPersonalizeDrawer?.isAvailable?.(this.closest('product-info') || document)) {
            window.PdpPersonalizeDrawer.open(this.closest('product-info') || document);
            return;
          }

          const sync = window.PdpNameVariantSync;
          sync?.openCustomeowEditor?.(this.closest('product-info') || document, evt);
          return;
        }

        evt.preventDefault();
        if (this.cartAddInFlight || this.form.dataset.themeCartAddInFlight === 'true') return;

        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          // Clear only a stale third-party lock. Never clear delivery or our own request lock.
          if (
            !this.isCustomizeNowIntent(evt) &&
            this.submitButton.dataset.themeCartAddLock !== 'true' &&
            !this.submitButton.hasAttribute('data-hadiyah-delivery-disabled')
          ) {
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            return;
          }
        }

        this.cartAddInFlight = true;
        this.form.dataset.themeCartAddInFlight = 'true';
        this.setSubmitLoading();
        this.dispatchCartAddLifecycle('start');
        this.handleErrorMessage();
        this.scheduleCheckoutPreparation();

        this.cartAddPromise = this.performCartAdd().finally(() => {
          this.cartAddInFlight = false;
          delete this.form.dataset.themeCartAddInFlight;
          this.clearSubmitLoading();
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.dispatchCartAddLifecycle('complete');
          this.cartAddPromise = null;
        });
      }

      async performCartAdd() {
        let formData;
        try {
          if (typeof window.fetchConfig !== 'function') {
            throw new Error('The storefront is still loading. Please try again.');
          }

          const config = window.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          formData = new FormData(this.form);
          const useAddonDrawer = Boolean(
            window.CartAddonDrawer?.openWithCartResponse || window.__ensureCartAddonDrawer
          );

        // The drawer may be rendered/upgraded after this component was constructed.
          this.cart = this.cart || document.querySelector('cart-drawer');

          if (this.cart) {
          // The add-on flow prepares cart sections in the background. Avoid
          // making Shopify render the full cart drawer before it can open.
            if (!useAddonDrawer && typeof this.cart.getSectionsToRender === 'function') {
              formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
              formData.append('sections_url', window.location.pathname);
            }
            this.cart.setActiveElement?.(document.activeElement);
          }

          this.appendExternalCustomizationProperties(formData);
          this.dedupeLineProperties(formData);
          config.body = formData;
          const baselineQuantity = this.captureCartBaseline(formData);

        // Only resolve CartAddonDrawer when we actually intend to open add-ons.
        // Prefetch left window.CartAddonDrawer set, which previously opened add-ons
        // even for Customize Now (hijab hampers etc.).
          const addonReady = useAddonDrawer && window.__ensureCartAddonDrawer
            ? window.__ensureCartAddonDrawer()
            : Promise.resolve(window.CartAddonDrawer || null);

          let response;
          try {
            response = await this.requestCartAdd(`${routes.cart_add_url}`, config);
          } catch (requestError) {
            if (!this.isAmbiguousNetworkError(requestError)) throw requestError;
            response = await this.reconcileAmbiguousCartAdd(formData, baselineQuantity);
            if (!response) throw requestError;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form-component',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }
          this.error = false;
          this.dispatchCartAddLifecycle('success');
          this.clearSubmittedCustomizationProperties();

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            await new Promise((resolve) => {
              document.body.addEventListener('modalClosed', resolve, { once: true });
              quickAddModal.hide(true);
            });
          }
          try {
            await this.openPostAddFlow(response, useAddonDrawer, addonReady);
          } catch (drawerError) {
            console.error(drawerError);
            this.handleErrorMessage('Your item was added, but the cart could not open. Please use the cart icon to continue.');
            this.dispatchCartAddLifecycle('post-add-error', {
              message: 'Your item was added, but the cart could not open.',
            });
          }
          return response;
        } catch (error) {
          console.error(error);
          const isAmbiguousNetworkFailure = this.isAmbiguousNetworkError(error);
          const message = isAmbiguousNetworkFailure
            ? 'We could not confirm the cart update. Please check your cart before trying again.'
            : error?.message || 'Something went wrong while adding this item. Please try again.';
          this.error = true;
          this.handleErrorMessage(message);
          this.publishCartError(formData, message, error?.cartResponse || {});
          this.dispatchCartAddLifecycle('error', { message });
          return null;
        }
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (
          this.cartAddInFlight ||
          this.form?.dataset.themeCartAddInFlight === 'true' ||
          this.submitButton.dataset.themeCartAddLock === 'true'
        ) {
          return;
        }

        const currentLabel = (this.submitButtonText?.textContent || '').trim().toLowerCase();
        const isCustomizeLabel =
          currentLabel.includes('customiz') || currentLabel.includes('customis');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && !isCustomizeLabel) this.submitButtonText.textContent = text;
          return;
        }

        this.submitButton.removeAttribute('disabled');
        // Never clobber Customize Now — pdp-name-variant-sync owns that label.
        // Callers pass soldOut as the 2nd arg even when enabling; ignore it here.
        if (!isCustomizeLabel) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
